import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { Action } from '@reduxjs/toolkit';
import { createSimpleMiddleware } from './createSimpleMiddleware';
import { AppState } from './store';
import { postLtcPlanningQuotesQueryResponse } from './ltcPlanningQuotesModel';
import {
  createQuoteWithROIandProjectedCoverage,
  createTraditionalLTCQuoteWithROIandProjectedCoverage,
} from '@client/util/policyQuoteCalculations';
import { formatROI } from '@shared';
import type { Quote } from './quotesModel';

/**
 * Middleware that intercepts LTC planning quotes responses and enriches them with ROI calculations
 * This ensures ROI is calculated once when quotes are fetched, not on every render
 */
function enrichLtcPlanningQuotesMiddlewareImpl(
  dispatch: Dispatch<AnyAction>,
  getStore: () => AppState,
  next: Dispatch<AnyAction>,
  action: Action<string>,
) {
  // Check if this is a quotes response action
  if (action.type === postLtcPlanningQuotesQueryResponse.type) {
    const state = getStore();
    const client = state.client.data;
    const quotesPayload = (action as any).payload as Quote[];

    if (!client || !quotesPayload?.length) {
      // No client or no quotes, pass through unchanged
      next(action);
      return;
    }

    try {
      // Enrich quotes with ROI calculations
      const enrichedQuotes = quotesPayload
        .map(quote => {
          try {
            // Check if this is a traditional LTC quote
            const isTraditionalLTC = !!(quote as any).traditionalLTCQuoteId;

            const enriched = isTraditionalLTC
              ? createTraditionalLTCQuoteWithROIandProjectedCoverage(quote as any, client, 'total')
              : createQuoteWithROIandProjectedCoverage(quote, client, 'total');

            const formattedROI = formatROI(enriched.roiValues, 'total');

            return {
              ...enriched,
              formattedROI,
            };
          } catch (error) {
            console.error(
              'Error calculating ROI for quote:',
              (quote as any).insuranceQuoteId || (quote as any).traditionalLTCQuoteId,
              error,
            );
            return null;
          }
        })
        .filter((q): q is NonNullable<typeof q> => q !== null);

      // Dispatch the enriched quotes instead of the original payload
      next({
        ...action,
        payload: enrichedQuotes,
      });
    } catch (error) {
      console.error('[enrichLtcPlanningQuotesMiddleware] Error enriching quotes:', error);
      // On error, pass through original action
      next(action);
    }
  } else {
    // Not a quotes response, pass through
    next(action);
  }
}

export const enrichLtcPlanningQuotesMiddleware = createSimpleMiddleware(
  enrichLtcPlanningQuotesMiddlewareImpl,
);
